import { ClientDto, DemandOrderDto, OrderDto, OrderStatus, ReturnOrderDto, SiteDto } from '../../api/ClientApi';
import { CompleteOrderType } from '../../common/Types';

export type OrderStatusFilterType = {
    Idle: boolean;
    Pending: boolean;
    Approved: boolean;
    Transport: boolean;
    Delivered: boolean;
    Returned: boolean;
    Cancelled: boolean;
};

export type OrderTypeFilterType = {
    Transfer: boolean;
    Reception: boolean;
    Demand: boolean;
    GeneralReturn: boolean;
};

export const getColorForOrderStatus = (orderStatus: OrderStatus) => {
    switch (orderStatus) {
        case OrderStatus.Idle:
            return 'default';
        case OrderStatus.Pending:
            return 'info';
        case OrderStatus.Approved:
            return 'primary';
        case OrderStatus.Transport:
            return 'secondary';
        case OrderStatus.Delivered:
            return 'success';
        case OrderStatus.Returned:
            return 'warning';
        case OrderStatus.Cancelled:
            return 'error';
        default:
            return 'error';
    }
};

export const getColorForOrderType = (orderType: CompleteOrderType) => {
    switch (orderType) {
        case CompleteOrderType.Transfer:
            return 'primary';
        case CompleteOrderType.Reception:
            return 'secondary';
        case CompleteOrderType.Demand:
            return 'warning';
        case CompleteOrderType.GeneralReturn:
            return 'success';
        default:
            return 'error';
    }
};

export const getClientNameInfo = (client?: ClientDto) => {
    return client ? (client.shortName ?? client.name ?? undefined) : undefined;
};

export const getSiteOrdersTableData = (site: SiteDto, sites?: SiteDto[], clients?: ClientDto[]): string => {
    const currSite = sites?.find((s) => s.id === site.id);
    const client = clients?.find((c) => c.id === currSite?.project?.clientId);

    const clientData = getClientNameInfo(client);

    return site.name ? (clientData ? site.name + ' (' + clientData + ')' : site.name) : (clientData ?? '/');
};

export const filterOrders = (
    orders: OrderDto[],
    orderTypeFilter: OrderTypeFilterType,
    orderStatusFilter: OrderStatusFilterType,
    originFilter: string,
    destinationFilter: string,
    clientFilter: string,
    clients: ClientDto[],
    sites: SiteDto[],
): OrderDto[] => {
    return orders
        ?.filter((o) => {
            if (
                !(
                    orderTypeFilter.Demand ||
                    orderTypeFilter.Reception ||
                    orderTypeFilter.GeneralReturn ||
                    orderTypeFilter.Transfer
                )
            ) {
                return true;
            }

            let returnType = false;

            if (orderTypeFilter.Reception) {
                returnType ||= !!o.supplierId;
            }

            if (orderTypeFilter.Transfer) {
                returnType ||= !!o.originSiteId;
            }

            return returnType;
        })
        .filter((o) => {
            if (!o.orderStatus) {
                return true;
            }

            if (
                !(
                    orderStatusFilter.Idle ||
                    orderStatusFilter.Approved ||
                    orderStatusFilter.Pending ||
                    orderStatusFilter.Transport ||
                    orderStatusFilter.Delivered ||
                    orderStatusFilter.Returned ||
                    orderStatusFilter.Cancelled
                )
            ) {
                return (
                    o.orderStatus !== OrderStatus.Delivered &&
                    o.orderStatus !== OrderStatus.Cancelled &&
                    o.orderStatus !== OrderStatus.Returned
                );
            }

            let returnStatus = false;

            if (orderStatusFilter.Idle) {
                returnStatus ||= o.orderStatus === OrderStatus.Idle;
            }

            if (orderStatusFilter.Approved) {
                returnStatus ||= o.orderStatus === OrderStatus.Approved;
            }

            if (orderStatusFilter.Pending) {
                returnStatus ||= o.orderStatus === OrderStatus.Pending;
            }

            if (orderStatusFilter.Transport) {
                returnStatus ||= o.orderStatus === OrderStatus.Transport;
            }

            if (orderStatusFilter.Delivered) {
                returnStatus ||= o.orderStatus === OrderStatus.Delivered;
            }

            if (orderStatusFilter.Returned) {
                returnStatus ||= o.orderStatus === OrderStatus.Returned;
            }

            if (orderStatusFilter.Cancelled) {
                returnStatus ||= o.orderStatus === OrderStatus.Cancelled;
            }

            return returnStatus;
        })
        .filter((o) => {
            const trimmedOriginFilter = originFilter.trim().toLowerCase();
            if (trimmedOriginFilter === '') {
                return true;
            }
            return (
                o.originSite?.name?.toLowerCase().includes(trimmedOriginFilter) ||
                o.supplier?.shortName?.toLowerCase().includes(trimmedOriginFilter) ||
                o.supplier?.name?.toLowerCase().includes(trimmedOriginFilter)
            );
        })
        .filter((o) => {
            const trimmedDestinationFilter = destinationFilter.trim().toLowerCase();
            if (trimmedDestinationFilter === '') {
                return true;
            }
            return o.destinationSite?.name?.toLowerCase().includes(trimmedDestinationFilter);
        })
        .filter((o) => {
            const trimmedClientFilter = clientFilter.trim().toLowerCase();

            if (trimmedClientFilter === '') {
                return true;
            }

            const originSite = sites.find((s) => s.id === o.originSiteId);
            const destinationSite = sites.find((s) => s.id === o.destinationSiteId);

            let filterBool = false;

            if (!!originSite) {
                const client = clients.find((c) => c.id === originSite.project?.clientId);

                if (!!client) {
                    if (
                        !!client.shortName?.toLowerCase().includes(trimmedClientFilter) ||
                        !!client.name?.toLowerCase().includes(trimmedClientFilter)
                    ) {
                        filterBool = true;
                    }
                }
            }

            if (!!destinationSite) {
                const client = clients.find((c) => c.id === destinationSite.project?.clientId);

                if (!!client) {
                    if (
                        !!client.shortName?.toLowerCase().includes(trimmedClientFilter) ||
                        !!client.name?.toLowerCase().includes(trimmedClientFilter)
                    ) {
                        filterBool = true;
                    }
                }
            }

            return filterBool;
        });
};

export const filterReturnOrders = (
    orders: ReturnOrderDto[],
    orderTypeFilter: OrderTypeFilterType,
    orderStatusFilter: OrderStatusFilterType,
    originFilter: string,
    clientFilter: string,
    clients: ClientDto[],
    sites: SiteDto[],
    isHighestPrivilageWarehouseOwner: boolean,
): ReturnOrderDto[] => {
    if (
        (isHighestPrivilageWarehouseOwner && !orderTypeFilter.GeneralReturn) ||
        ((orderTypeFilter.Demand ||
            orderTypeFilter.Reception ||
            orderTypeFilter.GeneralReturn ||
            orderTypeFilter.Transfer) &&
            !orderTypeFilter.GeneralReturn) ||
        orderStatusFilter.Cancelled ||
        orderStatusFilter.Delivered ||
        orderStatusFilter.Idle ||
        orderStatusFilter.Approved ||
        orderStatusFilter.Pending ||
        orderStatusFilter.Returned ||
        orderStatusFilter.Transport
    ) {
        return [];
    }

    return orders
        .filter((o) => {
            const trimmedOriginFilter = originFilter.trim().toLowerCase();
            if (trimmedOriginFilter === '') {
                return true;
            }
            return o.originSite?.name?.toLowerCase().includes(trimmedOriginFilter);
        })
        .filter((o) => {
            const trimmedClientFilter = clientFilter.trim().toLowerCase();

            if (trimmedClientFilter === '') {
                return true;
            }

            const originSite = sites.find((s) => s.id === o.originSiteId);

            let filterBool = false;

            if (!!originSite) {
                const client = clients.find((c) => c.id === originSite.project?.clientId);

                if (!!client) {
                    if (
                        !!client.shortName?.toLowerCase().includes(trimmedClientFilter) ||
                        !!client.name?.toLowerCase().includes(trimmedClientFilter)
                    ) {
                        filterBool = true;
                    }
                }
            }

            return filterBool;
        });
};

export const filterDemandOrders = (
    orders: DemandOrderDto[],
    orderTypeFilter: OrderTypeFilterType,
    orderStatusFilter: OrderStatusFilterType,
    destinationFilter: string,
    clientFilter: string,
    clients: ClientDto[],
    sites: SiteDto[],
    isHighestPrivilageWarehouseOwner: boolean,
): DemandOrderDto[] => {
    if (
        (isHighestPrivilageWarehouseOwner && !orderTypeFilter.Demand) ||
        ((orderTypeFilter.Demand ||
            orderTypeFilter.Reception ||
            orderTypeFilter.GeneralReturn ||
            orderTypeFilter.Transfer) &&
            !orderTypeFilter.Demand) ||
        orderStatusFilter.Cancelled ||
        orderStatusFilter.Delivered ||
        orderStatusFilter.Idle ||
        orderStatusFilter.Approved ||
        orderStatusFilter.Pending ||
        orderStatusFilter.Returned ||
        orderStatusFilter.Transport
    ) {
        return [];
    }

    return orders
        .filter((o) => {
            const trimmedDestinationFilter = destinationFilter.trim().toLowerCase();
            if (trimmedDestinationFilter === '') {
                return true;
            }
            return o.destinationSite?.name?.toLowerCase().includes(trimmedDestinationFilter);
        })
        .filter((o) => {
            const trimmedClientFilter = clientFilter.trim().toLowerCase();

            if (trimmedClientFilter === '') {
                return true;
            }

            const destinationSite = sites.find((s) => s.id === o.destinationSiteId);

            let filterBool = false;

            if (!!destinationSite) {
                const client = clients.find((c) => c.id === destinationSite.project?.clientId);

                if (!!client) {
                    if (
                        !!client.shortName?.toLowerCase().includes(trimmedClientFilter) ||
                        !!client.name?.toLowerCase().includes(trimmedClientFilter)
                    ) {
                        filterBool = true;
                    }
                }
            }

            return filterBool;
        });
};
